import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronUp, Upload } from "lucide-react";
import { API, BACKEND_URL } from "@/constants/content";
import { useSiteContent, DEFAULTS } from "@/hooks/useSiteContent";

const TABS = [
  { key: "studio", label: "Stúdió" },
  { key: "magnets", label: "Magnetek" },
  { key: "massages", label: "Masszázsok" },
  { key: "fourlife", label: "4Life csomagok" },
  { key: "highlights", label: "Kiemelt csomagok" },
  { key: "forever", label: "Forever oldal" },
  { key: "articles", label: "Blog cikkek" },
  { key: "videos", label: "Blog videók" },
];

const ICON_OPTIONS = ["Sparkles", "Heart", "Leaf", "Sun", "Dumbbell", "GraduationCap"];

const inputCls =
  "w-full bg-white border border-[#EAE5D9] focus:border-[#2A9D8F] focus:ring-1 focus:ring-[#2A9D8F] outline-none rounded-xl px-3 py-2 text-sm text-[#1A2825]";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-[#758C88]";

// ----- helpers -----
function Field({ label, value, onChange, type = "text", placeholder, testId, hint, rows }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {type === "textarea" ? (
        <textarea
          rows={rows || 3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={`${inputCls} mt-1 leading-relaxed`}
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={`${inputCls} mt-1`}
        />
      )}
      {hint && <div className="text-[10px] text-[#758C88] mt-1 italic">{hint}</div>}
    </div>
  );
}

function ImageField({ label, value, onChange, token, testId, hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "X-Admin-Token": token } });
      onChange(data.url);
      toast.success("Kép feltöltve!");
    } catch (err) {
      toast.error("Feltöltés sikertelen: " + (err?.response?.data?.detail || "Hiba"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const src = value ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`) : "";
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="mt-1 flex items-start gap-3">
        {src && (
          <img src={src} alt="" className="h-16 w-24 object-cover rounded-lg border border-[#EAE5D9] flex-shrink-0" />
        )}
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://... vagy tölts fel egy képet"
            data-testid={testId}
            className={inputCls}
          />
          <div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid={`${testId}-file`}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testId}-upload`}
              className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#EAE5D9] hover:border-[#2A9D8F] text-[#3A4D4A] px-3 py-1.5 rounded-full disabled:opacity-50 transition-colors"
            >
              <Upload className="h-3.5 w-3.5" /> {uploading ? "Feltöltés..." : "Kép feltöltése"}
            </button>
          </div>
        </div>
      </div>
      {hint && <div className="text-[10px] text-[#758C88] mt-1 italic">{hint}</div>}
    </div>
  );
}

function ArrayField({ label, values = [], onChange, placeholder, testId }) {
  // Each line = one item
  return (
    <div>
      <label className={labelCls}>{label} <span className="text-[#A88A2B] normal-case tracking-normal">(egy sor = egy elem)</span></label>
      <textarea
        rows={Math.max(3, values.length + 1)}
        value={(values || []).join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trimEnd()).filter((s, i, arr) => s || i < arr.length - 1))}
        placeholder={placeholder}
        data-testid={testId}
        className={`${inputCls} mt-1 leading-relaxed font-mono text-xs`}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`${inputCls} mt-1`}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function CardAccordion({ title, subtitle, children, defaultOpen = false, onDelete }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((v) => !v);
  return (
    <div className="bg-white rounded-2xl border border-[#EAE5D9] overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-[#F6F3EB]/50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30"
      >
        <div>
          <div className="font-medium text-[#1A2825]">{title || "Új elem"}</div>
          {subtitle && <div className="text-xs text-[#758C88] mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          {onDelete && open && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (window.confirm("Biztosan törlöd ezt az elemet?")) onDelete(); }}
              className="p-2 rounded-full hover:bg-red-50 text-red-600"
              aria-label="Törlés"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-[#758C88]" /> : <ChevronDown className="h-4 w-4 text-[#758C88]" />}
        </div>
      </div>
      {open && <div className="px-5 pb-5 pt-2 border-t border-[#EAE5D9] space-y-4">{children}</div>}
    </div>
  );
}

// ----- Section editors -----
function StudioEditor({ data, set, token }) {
  const update = (patch) => set({ ...data, ...patch });
  const updatePract = (idx, patch) => {
    const next = [...(data.practitioners || [])];
    next[idx] = { ...next[idx], ...patch };
    set({ ...data, practitioners: next });
  };
  const removePract = (idx) => {
    const next = [...(data.practitioners || [])];
    next.splice(idx, 1);
    set({ ...data, practitioners: next });
  };
  const addPract = () => {
    set({ ...data, practitioners: [...(data.practitioners || []), { name: "", phone: "", phoneDisplay: "" }] });
  };
  return (
    <div className="space-y-5">
      <Field label="Stúdió neve" value={data.name} onChange={(v) => update({ name: v })} testId="content-studio-name" />
      <Field label="Cím" value={data.address} onChange={(v) => update({ address: v })} testId="content-studio-address" />
      <Field label="Kapcsolat szöveg" value={data.contactNote} onChange={(v) => update({ contactNote: v })} testId="content-studio-contact" />

      <div className="pt-4 border-t border-[#EAE5D9]">
        <div className={labelCls + " mb-3"}>Bemutatkozás oldal képei (Rólunk)</div>
        <div className="space-y-4">
          <ImageField label="Rita fotó" value={data.photoRita || ""} onChange={(v) => update({ photoRita: v })} token={token} testId="content-studio-photo-rita" />
          <ImageField label="Annamari fotó" value={data.photoAnnamari || ""} onChange={(v) => update({ photoAnnamari: v })} token={token} testId="content-studio-photo-annamari" />
          <ImageField label="Stúdió belső 1 (nagy, felül)" value={data.photoStudio1 || ""} onChange={(v) => update({ photoStudio1: v })} token={token} testId="content-studio-photo1" />
          <ImageField label="Stúdió belső 2 (kicsi, bal)" value={data.photoStudio2 || ""} onChange={(v) => update({ photoStudio2: v })} token={token} testId="content-studio-photo2" />
          <ImageField label="Stúdió belső 3 (kicsi, jobb)" value={data.photoStudio3 || ""} onChange={(v) => update({ photoStudio3: v })} token={token} testId="content-studio-photo3" />
        </div>
      </div>

      <div className="pt-4 border-t border-[#EAE5D9]">
        <div className="flex items-center justify-between mb-3">
          <div className={labelCls}>Munkatársak</div>
          <button type="button" onClick={addPract} className="inline-flex items-center gap-1 text-xs text-[#2A9D8F] hover:text-[#1C6B61]">
            <Plus className="h-3.5 w-3.5" /> Új munkatárs
          </button>
        </div>
        <div className="space-y-3">
          {(data.practitioners || []).map((p, i) => (
            <CardAccordion
              key={i}
              title={p.name || `Munkatárs ${i + 1}`}
              subtitle={p.phoneDisplay}
              defaultOpen
              onDelete={() => removePract(i)}
            >
              <Field label="Név" value={p.name} onChange={(v) => updatePract(i, { name: v })} testId={`content-practitioner-${i}-name`} />
              <Field label="Telefon (E.164, pl. +36...)" value={p.phone} onChange={(v) => updatePract(i, { phone: v })} placeholder="+36704324269" testId={`content-practitioner-${i}-phone`} />
              <Field label="Telefon (megjelenítendő szöveg)" value={p.phoneDisplay} onChange={(v) => updatePract(i, { phoneDisplay: v })} placeholder="06 70 / 432-4269" testId={`content-practitioner-${i}-display`} />
            </CardAccordion>
          ))}
        </div>
      </div>
    </div>
  );
}

function ListEditor({ items = [], set, render, defaultItem, itemLabel = "Új elem", testIdPrefix = "item" }) {
  const update = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    set(next);
  };
  const remove = (i) => {
    const next = [...items];
    next.splice(i, 1);
    set(next);
  };
  const add = () => set([...items, { ...defaultItem, key: `${defaultItem.key || "uj"}-${Date.now()}` }]);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-[#758C88]">{items.length} db elem</div>
        <button type="button" onClick={add} data-testid={`${testIdPrefix}-add`} className="inline-flex items-center gap-1 text-xs text-[#2A9D8F] hover:text-[#1C6B61]">
          <Plus className="h-3.5 w-3.5" /> Új elem
        </button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <CardAccordion
            key={item.key || i}
            title={item.title || itemLabel}
            subtitle={item.subtitle || item.short || item.tagline || item.duration || item.category || ""}
            defaultOpen={items.length === 1}
            onDelete={() => remove(i)}
          >
            {render(item, (patch) => update(i, patch), i)}
          </CardAccordion>
        ))}
      </div>
    </div>
  );
}

function MagnetsEditor({ data, set }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új magnet"
      testIdPrefix="content-magnet"
      defaultItem={{ key: "uj", icon: "Sparkles", badge: "", title: "", subtitle: "", description: "", bullets: [], accent: "#2A9D8F" }}
      render={(item, update, i) => (
        <>
          <Field label="Kulcs (azonosító, csak betű/szám/kötőjel)" value={item.key} onChange={(v) => update({ key: v })} testId={`content-magnet-${i}-key`} />
          <Field label="Címke (badge)" value={item.badge} onChange={(v) => update({ badge: v })} testId={`content-magnet-${i}-badge`} />
          <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-magnet-${i}-title`} />
          <Field label="Alcím" value={item.subtitle} onChange={(v) => update({ subtitle: v })} testId={`content-magnet-${i}-subtitle`} />
          <Field label="Leírás" type="textarea" value={item.description} onChange={(v) => update({ description: v })} testId={`content-magnet-${i}-desc`} />
          <ArrayField label="Pontok" values={item.bullets} onChange={(v) => update({ bullets: v })} testId={`content-magnet-${i}-bullets`} />
          <Select label="Ikon" value={item.icon} onChange={(v) => update({ icon: v })} options={ICON_OPTIONS} testId={`content-magnet-${i}-icon`} />
          <Field label="Kiemelő szín (HEX)" value={item.accent} onChange={(v) => update({ accent: v })} placeholder="#2A9D8F" testId={`content-magnet-${i}-accent`} />
        </>
      )}
    />
  );
}

function MassagesEditor({ data, set }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új masszázs"
      testIdPrefix="content-massage"
      defaultItem={{ key: "uj", title: "", short: "", duration: "", price: "", description: "", benefits: [], accent: "#2A9D8F" }}
      render={(item, update, i) => (
        <>
          <Field label="Kulcs" value={item.key} onChange={(v) => update({ key: v })} testId={`content-massage-${i}-key`} />
          <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-massage-${i}-title`} />
          <Field label="Rövid leírás" value={item.short} onChange={(v) => update({ short: v })} testId={`content-massage-${i}-short`} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Időtartam" value={item.duration} onChange={(v) => update({ duration: v })} placeholder="50 perc" testId={`content-massage-${i}-duration`} />
            <Field label="Ár" value={item.price} onChange={(v) => update({ price: v })} placeholder="12 000 Ft-tól" testId={`content-massage-${i}-price`} />
          </div>
          <Field label="Részletes leírás" type="textarea" value={item.description} onChange={(v) => update({ description: v })} testId={`content-massage-${i}-desc`} />
          <ArrayField label="Előnyök" values={item.benefits} onChange={(v) => update({ benefits: v })} testId={`content-massage-${i}-benefits`} />
          <Field label="Kiemelő szín (HEX)" value={item.accent} onChange={(v) => update({ accent: v })} testId={`content-massage-${i}-accent`} />
        </>
      )}
    />
  );
}

function FourLifeEditor({ data, set }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új 4Life csomag"
      testIdPrefix="content-4life"
      defaultItem={{ key: "uj", title: "", tagline: "", description: "", highlights: [], accent: "#A88A2B" }}
      render={(item, update, i) => (
        <>
          <Field label="Kulcs" value={item.key} onChange={(v) => update({ key: v })} testId={`content-4life-${i}-key`} />
          <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-4life-${i}-title`} />
          <Field label="Alcím" value={item.tagline} onChange={(v) => update({ tagline: v })} testId={`content-4life-${i}-tagline`} />
          <Field label="Leírás" type="textarea" value={item.description} onChange={(v) => update({ description: v })} testId={`content-4life-${i}-desc`} />
          <ArrayField label="Kiemelt pontok" values={item.highlights} onChange={(v) => update({ highlights: v })} testId={`content-4life-${i}-highlights`} />
          <Field label="Kiemelő szín (HEX)" value={item.accent} onChange={(v) => update({ accent: v })} testId={`content-4life-${i}-accent`} />
        </>
      )}
    />
  );
}

function HighlightsEditor({ data, set, token }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új kiemelt csomag"
      testIdPrefix="content-highlight"
      defaultItem={{ key: "uj", badge: "", title: "", intro: "", cover: "", accent: "#2A9D8F", products: [], articleTo: "/blog", articleLabel: "Tovább" }}
      render={(item, update, i) => {
        const products = item.products || [];
        const updateProduct = (pi, patch) => {
          const next = [...products];
          next[pi] = { ...next[pi], ...patch };
          update({ products: next });
        };
        const removeProduct = (pi) => {
          const next = [...products];
          next.splice(pi, 1);
          update({ products: next });
        };
        const addProduct = () => update({ products: [...products, { name: "", text: "" }] });
        return (
          <>
            <Field label="Kulcs (azonosító)" value={item.key} onChange={(v) => update({ key: v })} testId={`content-highlight-${i}-key`} />
            <Field label="Címke (badge)" value={item.badge} onChange={(v) => update({ badge: v })} placeholder="Gyerekeknek · Suli-szezon" testId={`content-highlight-${i}-badge`} />
            <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-highlight-${i}-title`} />
            <ImageField label="Borítókép" value={item.cover} onChange={(v) => update({ cover: v })} token={token} hint="A szekció tetején megjelenő kép. Tölts fel egyet, vagy illessz be egy URL-t. Ha üres, kép nélkül jelenik meg." testId={`content-highlight-${i}-cover`} />
            <Field label="Bevezető szöveg" type="textarea" rows={4} value={item.intro} onChange={(v) => update({ intro: v })} testId={`content-highlight-${i}-intro`} />
            <Field label="Kiemelő szín (HEX)" value={item.accent} onChange={(v) => update({ accent: v })} placeholder="#2A9D8F" testId={`content-highlight-${i}-accent`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Link cél (útvonal)" value={item.articleTo} onChange={(v) => update({ articleTo: v })} placeholder="/blog" hint="Pl. /blog vagy /regeneracios-kisokos" testId={`content-highlight-${i}-articleto`} />
              <Field label="Link felirat" value={item.articleLabel} onChange={(v) => update({ articleLabel: v })} placeholder="Elolvasom a cikket" testId={`content-highlight-${i}-articlelabel`} />
            </div>
            <div className="pt-3 border-t border-[#EAE5D9]">
              <div className="flex items-center justify-between mb-3">
                <div className={labelCls}>Termékek a csomagban</div>
                <button type="button" onClick={addProduct} data-testid={`content-highlight-${i}-add-product`} className="inline-flex items-center gap-1 text-xs text-[#2A9D8F] hover:text-[#1C6B61]">
                  <Plus className="h-3.5 w-3.5" /> Új termék
                </button>
              </div>
              <div className="space-y-3">
                {products.map((p, pi) => (
                  <CardAccordion key={pi} title={p.name || `Termék ${pi + 1}`} defaultOpen onDelete={() => removeProduct(pi)}>
                    <Field label="Termék neve" value={p.name} onChange={(v) => updateProduct(pi, { name: v })} testId={`content-highlight-${i}-product-${pi}-name`} />
                    <Field label="Leírás (miért jó)" type="textarea" value={p.text} onChange={(v) => updateProduct(pi, { text: v })} testId={`content-highlight-${i}-product-${pi}-text`} />
                  </CardAccordion>
                ))}
              </div>
            </div>
          </>
        );
      }}
    />
  );
}

function ForeverPillarsEditor({ data, set }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új pillér"
      testIdPrefix="content-forever-pillar"
      defaultItem={{ title: "", text: "" }}
      render={(item, update, i) => (
        <>
          <Field label="Pillér címe" value={item.title} onChange={(v) => update({ title: v })} testId={`content-forever-pillar-${i}-title`} />
          <Field label="Leírás" type="textarea" value={item.text} onChange={(v) => update({ text: v })} testId={`content-forever-pillar-${i}-text`} />
        </>
      )}
    />
  );
}

function ForeverPackagesEditor({ data, set, token }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új csomag"
      testIdPrefix="content-forever-package"
      defaultItem={{ key: "uj-csomag", accent: "#2A9D8F", title: "", tagline: "", description: "", contents: [], benefit: "", articleKey: "borapolas-szinergia", cover: "" }}
      render={(item, update, i) => (
        <>
          <Field label="Kulcs (azonosító)" value={item.key} onChange={(v) => update({ key: v })} testId={`content-forever-package-${i}-key`} />
          <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-forever-package-${i}-title`} />
          <Field label="Alcím (tagline)" value={item.tagline} onChange={(v) => update({ tagline: v })} testId={`content-forever-package-${i}-tagline`} />
          <Field label="Rövid leírás" type="textarea" value={item.description} onChange={(v) => update({ description: v })} testId={`content-forever-package-${i}-description`} />
          <ArrayField label="A csomag tartalma" values={item.contents} onChange={(arr) => update({ contents: arr })} placeholder={"Aloe Liquid Soap\nAloe First"} testId={`content-forever-package-${i}-contents`} />
          <Field label="Mire jó?" type="textarea" value={item.benefit} onChange={(v) => update({ benefit: v })} testId={`content-forever-package-${i}-benefit`} />
          <Field label="Kapcsolódó cikk kulcsa" value={item.articleKey} onChange={(v) => update({ articleKey: v })} placeholder="borapolas-szinergia" hint="Az „Elolvasom a teljes cikket” gomb ezt a blog cikket nyitja meg (a cikk kulcsa a Blog cikkek fülön látható)." testId={`content-forever-package-${i}-articlekey`} />
          <Field label="Kiemelő szín (HEX)" value={item.accent} onChange={(v) => update({ accent: v })} placeholder="#2A9D8F" testId={`content-forever-package-${i}-accent`} />
          <ImageField label="Borítókép (opcionális)" value={item.cover} onChange={(v) => update({ cover: v })} token={token} hint="Nem kötelező. Ha megadod, a kártyán is megjeleníthető." testId={`content-forever-package-${i}-cover`} />
        </>
      )}
    />
  );
}

function ArticlesEditor({ data, set, token }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új cikk"
      testIdPrefix="content-article"
      defaultItem={{ key: "uj", title: "", excerpt: "", body: "", date: "", category: "", cover: "", ctaLabel: "", ctaHref: "" }}
      render={(item, update, i) => (
        <>
          <Field label="Kulcs" value={item.key} onChange={(v) => update({ key: v })} testId={`content-article-${i}-key`} />
          <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-article-${i}-title`} />
          <Field label="Kategória" value={item.category} onChange={(v) => update({ category: v })} testId={`content-article-${i}-category`} />
          <Field label="Dátum" value={item.date} onChange={(v) => update({ date: v })} placeholder="2026. március" testId={`content-article-${i}-date`} />
          <ImageField label="Borítókép" value={item.cover} onChange={(v) => update({ cover: v })} token={token} placeholder="https://..." testId={`content-article-${i}-cover`} />
          <Field
            label="Előnézet (kártyán mutatott rövid részlet)"
            type="textarea"
            rows={3}
            value={item.excerpt}
            onChange={(v) => update({ excerpt: v })}
            testId={`content-article-${i}-excerpt`}
            hint="2-3 mondat teaser. Ez jelenik meg a blog kártyán."
          />
          <Field
            label="Cikk teljes törzse"
            type="textarea"
            rows={12}
            value={item.body}
            onChange={(v) => update({ body: v })}
            testId={`content-article-${i}-body`}
            hint="A teljes cikk szövege. Ha üresen hagyod, a kártyán 'Hamarosan olvasható' marad. Ha kitöltöd, 'Tovább olvasom' gomb jelenik meg. HTML: <strong>vastag</strong>, <em>dőlt</em>, <br> új sor, <a href='...' target='_blank'>link</a>, <ul><li>...</li></ul> lista"
          />
          <Field
            label="CTA gomb felirata (opcionális)"
            value={item.ctaLabel}
            onChange={(v) => update({ ctaLabel: v })}
            testId={`content-article-${i}-cta-label`}
            placeholder="pl. Megnézem az illóolaj receptet"
            hint="Ha kitöltöd (mindkét mezőt), a cikk törzse alatt egy nagy zöld gomb jelenik meg."
          />
          <Field
            label="CTA gomb linkje (opcionális)"
            value={item.ctaHref}
            onChange={(v) => update({ ctaHref: v })}
            testId={`content-article-${i}-cta-href`}
            placeholder="pl. /aroma-flow-recept vagy https://m.me/..."
            hint="Belső oldal (/aroma-flow-recept) vagy külső URL (https://...) is lehet."
          />
        </>
      )}
    />
  );
}

function VideosEditor({ data, set, token }) {
  return (
    <ListEditor
      items={data}
      set={set}
      itemLabel="Új videó"
      testIdPrefix="content-video"
      defaultItem={{ key: "uj", title: "", duration: "", cover: "", facebook_url: "", articleKey: "" }}
      render={(item, update, i) => (
        <>
          <Field label="Kulcs" value={item.key} onChange={(v) => update({ key: v })} testId={`content-video-${i}-key`} />
          <Field label="Cím" value={item.title} onChange={(v) => update({ title: v })} testId={`content-video-${i}-title`} />
          <Field label="Hossz" value={item.duration} onChange={(v) => update({ duration: v })} placeholder="5 perc" testId={`content-video-${i}-duration`} />
          <ImageField label="Borítókép" value={item.cover} onChange={(v) => update({ cover: v })} token={token} placeholder="https://..." testId={`content-video-${i}-cover`} />
          <Field
            label="Facebook videó link"
            value={item.facebook_url}
            onChange={(v) => update({ facebook_url: v })}
            placeholder="https://www.facebook.com/..."
            testId={`content-video-${i}-facebook-url`}
            hint="Ha kitöltöd, a videó kártyára kattintva ez az URL nyílik meg új lapon. Ha üres, a fő Facebook oldal nyílik meg."
          />
          <Field
            label="Kapcsolódó cikk kulcsa (opcionális)"
            value={item.articleKey}
            onChange={(v) => update({ articleKey: v })}
            placeholder="pl. esti-nyugi-legzes"
            testId={`content-video-${i}-articlekey`}
            hint="Ha kitöltöd, a videó alatt megjelenik egy „Elolvasom a cikket” gomb, ami erre a cikkre visz. A cikk kulcsa a Blog cikkek fülön látható."
          />
        </>
      )}
    />
  );
}

// ----- Main editor -----
export default function ContentEditor({ token }) {
  const { content, refresh } = useSiteContent();
  const [active, setActive] = useState("studio");
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(content);
    setDirty(false);
  }, [content]);

  const updateSection = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, draft, { headers: { "X-Admin-Token": token } });
      toast.success("Mentve! A változások azonnal láthatók az oldalon.");
      setDirty(false);
      refresh();
    } catch (err) {
      toast.error("Mentés sikertelen: " + (err?.response?.data?.detail || "Hiba"));
    } finally {
      setSaving(false);
    }
  };

  const handleResetSection = () => {
    if (!window.confirm(`Biztosan visszaállítod az alapértelmezett szöveget a "${TABS.find((t) => t.key === active).label}" szekcióban? A mostani változtatások elvesznek.`)) return;
    if (active === "forever") {
      setDraft((d) => ({ ...d, foreverPillars: DEFAULTS.foreverPillars, foreverPackages: DEFAULTS.foreverPackages }));
    } else {
      setDraft((d) => ({ ...d, [active]: DEFAULTS[active] }));
    }
    setDirty(true);
  };

  const handleResetAll = async () => {
    if (!window.confirm("Biztosan visszaállítod a TELJES oldal szövegét az alapértelmezett állapotba? Ez nem visszafordítható.")) return;
    setSaving(true);
    try {
      await axios.post(`${API}/content/reset`, {}, { headers: { "X-Admin-Token": token } });
      toast.success("Visszaállítva alapértelmezettre.");
      refresh();
    } catch (err) {
      toast.error("Visszaállítás sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 border-b border-[#EAE5D9] pb-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            data-testid={`content-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm transition-all ${
              active === t.key
                ? "bg-[#2A9D8F] text-white shadow-md"
                : "bg-white border border-[#EAE5D9] text-[#3A4D4A] hover:border-[#2A9D8F]/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Active section */}
      <div className="bg-[#FDFBF7]/60 rounded-2xl p-5 md:p-6 border border-[#EAE5D9]">
        {active === "studio" && <StudioEditor data={draft.studio} set={(v) => updateSection("studio", v)} token={token} />}
        {active === "magnets" && <MagnetsEditor data={draft.magnets} set={(v) => updateSection("magnets", v)} />}
        {active === "massages" && <MassagesEditor data={draft.massages} set={(v) => updateSection("massages", v)} />}
        {active === "fourlife" && <FourLifeEditor data={draft.fourlife} set={(v) => updateSection("fourlife", v)} />}
        {active === "highlights" && <HighlightsEditor data={draft.highlights} set={(v) => updateSection("highlights", v)} token={token} />}
        {active === "forever" && (
          <div className="space-y-8">
            <div>
              <div className="text-sm font-medium text-[#1A2825] mb-3">A rutin 3 pillére</div>
              <ForeverPillarsEditor data={draft.foreverPillars} set={(v) => updateSection("foreverPillars", v)} />
            </div>
            <div className="pt-6 border-t border-[#EAE5D9]">
              <div className="text-sm font-medium text-[#1A2825] mb-3">Csomagok</div>
              <ForeverPackagesEditor data={draft.foreverPackages} set={(v) => updateSection("foreverPackages", v)} token={token} />
            </div>
          </div>
        )}
        {active === "articles" && <ArticlesEditor data={draft.articles} set={(v) => updateSection("articles", v)} token={token} />}
        {active === "videos" && <VideosEditor data={draft.videos} set={(v) => updateSection("videos", v)} token={token} />}
      </div>

      {/* Action bar */}
      <div className="sticky bottom-4 z-10 bg-white rounded-2xl border border-[#EAE5D9] shadow-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-xs text-[#758C88]">
          {dirty ? (
            <span className="text-[#A88A2B] font-medium">Vannak el nem mentett változások</span>
          ) : (
            <span>Minden mentve</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetSection}
            data-testid="content-reset-section"
            className="inline-flex items-center gap-1.5 bg-white border border-[#EAE5D9] hover:border-[#A88A2B] text-[#3A4D4A] px-4 py-2 rounded-full text-sm"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Szekció visszaállítása
          </button>
          <button
            type="button"
            onClick={handleResetAll}
            data-testid="content-reset-all"
            className="inline-flex items-center gap-1.5 bg-white border border-red-200 hover:border-red-400 text-red-600 px-4 py-2 rounded-full text-sm"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Teljes visszaállítás
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="content-save"
            className="inline-flex items-center gap-2 bg-[#2A9D8F] hover:bg-[#1C6B61] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-full text-sm font-medium shadow-md"
          >
            <Save className="h-4 w-4" />
            {saving ? "Mentés..." : "Mentés"}
          </button>
        </div>
      </div>
    </div>
  );
}
